/**
 * useArcheHandoffIntake — the ONE sanctioned consumer of the `fiasAiPrefill`
 * navigation state (Arche Handoff, UI plane — see ../handoff.ts and
 * docs/arche-interop.md). An arche page that supports intake calls this hook;
 * reading `location.state.fiasAiPrefill` anywhere else is forbidden (pinned by
 * tests/architecture/arche-handoff-registry.test.ts).
 *
 * Behavior:
 * - Fires AT MOST ONCE per navigation. TWO things enforce that, and both are
 *   needed. The history replace below clears the state so refresh /
 *   back-forward never re-apply a handoff (a re-opened composer after every
 *   refresh would be user-hostile). The applied-key ref additionally survives
 *   a re-invocation of THIS effect against the same history entry — under
 *   React StrictMode every effect runs twice in development, and `navigate`
 *   does not mutate the `location` already captured by the running closure,
 *   so the strip alone lets the second invocation see the payload and apply
 *   it again. That is not a dev-only curiosity: it made every handoff in
 *   development deliver twice, which for Document Sign meant one draft with
 *   two copies of the same PDF.
 * - Treats the payload as UNTRUSTED: any same-origin code can navigate with
 *   state. It re-validates with parseArcheHandoffPayload and checks the kind
 *   actually targets THIS arche; anything invalid is dropped loudly
 *   (breadcrumb) — never a crash, never a silent partial apply.
 * - Also surfaces the sibling `fiasAiHandoffContext` key, which carries what
 *   the HOST knows about the handoff rather than what the payload claims —
 *   today just the originating arche, stamped from the calling bridge's own
 *   id. It rides beside the payload rather than inside it so a producer
 *   cannot name its own origin. It is read HERE, and only here, for the same
 *   reason `fiasAiPrefill` is: an ad-hoc read elsewhere would skip the
 *   consume-once strip and the validation below.
 *
 *   Host-derived, not attested. Do not build an authorization decision on it.
 */

import { useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { addBreadcrumb } from '@/lib/telemetry';
import {
  handoffTargetsArche,
  parseArcheHandoffPayload,
  type ArcheHandoffPayload,
} from '../handoff';
import { handoffTargetsArcheAsync } from '../plugin-intake-directory';

/** What the host knows about a handoff, beside the payload itself. */
export interface ArcheHandoffContext {
  /** Arche that initiated the handoff. Host-derived; never attested. */
  originArcheId?: string;
}

/** Arche id shape, mirroring `isValidArcheIdFormat` in the plugin bridge. */
const ARCHE_ID_RE = /^(arc_[a-f0-9]{32}|arche_[a-z0-9_]+)$/;

function parseHandoffContext(value: unknown): ArcheHandoffContext {
  if (typeof value !== 'object' || value === null) return {};
  const { originArcheId } = value as { originArcheId?: unknown };
  if (typeof originArcheId !== 'string' || !ARCHE_ID_RE.test(originArcheId)) return {};
  return { originArcheId };
}

export function useArcheHandoffIntake(
  archeId: string,
  onHandoff: (payload: ArcheHandoffPayload, context: ArcheHandoffContext) => void,
  /**
   * `enabled: false` leaves the history entry's handoff UNTOUCHED — not
   * consumed, not stripped — for whoever is enabled when it is true again.
   * For a host that knows `archeId` is stale (PluginRenderer's `pending`):
   * consuming would test the payload against the wrong arche and drop it.
   */
  { enabled = true }: { enabled?: boolean } = {},
): void {
  const location = useLocation();
  const navigate = useNavigate();
  // The handler is read through a ref so a new closure each render doesn't
  // re-arm the effect (the location.key dep alone decides when to fire).
  const onHandoffRef = useRef(onHandoff);
  onHandoffRef.current = onHandoff;
  // The arche this hook currently serves. The host component is reused across
  // arche-to-arche navigation, so by the time an async target check settles
  // `onHandoffRef` may belong to a DIFFERENT arche — the payload is checked
  // against this before it is applied. (Not `location.key`: the consume-once
  // `navigate(..., { replace: true })` below changes it every time. Not effect
  // cleanup: under StrictMode the second run returns early on
  // `appliedKeyRef`, so cancelling the first run's check would lose the
  // handoff.)
  const archeIdRef = useRef(archeId);
  archeIdRef.current = archeId;
  // The history entry whose handoff this mount has already consumed.
  const appliedKeyRef = useRef<string | null>(null);

  useEffect(() => {
    const state = location.state as {
      fiasAiPrefill?: unknown;
      fiasAiHandoffContext?: unknown;
    } | null;
    const raw = state?.fiasAiPrefill;
    if (raw === undefined) return;
    if (!enabled) return;
    // Consumed already on this history entry — see the StrictMode note above.
    // Marked BEFORE any validation, because reaching here means the payload
    // is being consumed either way: an invalid one is dropped, not retried.
    if (appliedKeyRef.current === location.key) return;
    appliedKeyRef.current = location.key;
    const context = parseHandoffContext(state?.fiasAiHandoffContext);
    // Consume-once: strip the state from history BEFORE applying, so a
    // mid-handler crash or refresh can't replay the handoff.
    navigate(location.pathname + location.search, { replace: true, state: null });
    const payload = parseArcheHandoffPayload(raw);
    if (!payload) {
      addBreadcrumb('arche-handoff', 'Dropped invalid handoff payload', { archeId });
      return;
    }
    const apply = () => {
      // Record the SUCCESS too, not just the drops. This lane is one-way and
      // unacknowledged: without a breadcrumb here, "the intake never fired" and
      // "it fired and the payload was lost further down" leave identical
      // evidence — which is exactly the position a live handoff bug left us in.
      addBreadcrumb('arche-handoff', 'Applied handoff payload', {
        archeId,
        kind: payload.kind,
        originArcheId: context.originArcheId ?? null,
      });
      onHandoffRef.current(payload, context);
    };
    const drop = () =>
      addBreadcrumb('arche-handoff', 'Dropped mistargeted handoff payload', {
        archeId,
        kind: payload.kind,
      });
    if (handoffTargetsArche(payload, archeId)) {
      apply();
      return;
    }
    // A plugin file destination (ADR-030) is server-listed; on a fresh page
    // load the list may not have arrived yet. The payload is already held
    // here (the history state is gone), so wait for the list, then decide.
    if (payload.kind === 'open_document' && archeId.startsWith('arc_')) {
      void handoffTargetsArcheAsync(payload, archeId).then(
        (ok) => {
          if (archeIdRef.current !== archeId) {
            // Its own breadcrumb: "the page moved on" and "aimed at the wrong
            // arche" must not leave the same evidence.
            addBreadcrumb('arche-handoff', 'Dropped handoff: arche changed while it was checked', {
              archeId,
              kind: payload.kind,
            });
            return;
          }
          if (ok) apply();
          else drop();
        },
        () => drop(),
      );
      return;
    }
    drop();
    // location.key identifies THIS history entry — a fresh navigation (new
    // key) re-arms the intake; re-renders of the same entry do not. The other
    // values used above are deliberately NOT deps: `onHandoff` is read through
    // a ref, and `location`/`navigate` change identity on every render, which
    // would re-fire the intake and re-apply the handoff.
    //
    // No eslint-disable here: this repo does not install
    // eslint-plugin-react-hooks, so a directive naming
    // `react-hooks/exhaustive-deps` is itself a lint ERROR ("Definition for
    // rule ... was not found") and fails the pre-push lint.
    //
    // `enabled` IS a dep: a handoff left in place while disabled must be taken
    // when the host enables — on the same history entry, so `location.key`
    // alone would never re-fire.
  }, [location.key, enabled]);
}
